import {
  NO_CASH_VALUE_DISCLOSURE,
  NO_CASH_VALUE_DISCLOSURE_TL,
  STAKES,
  STAKE_TIERS,
  VENUES,
  type GameId,
  type LobbyResponse,
  type OpenTable,
  type StakeTier,
} from '@tira/protocol';
import { useEffect, useMemo, useState, type CSSProperties, type JSX } from 'react';
import { Button } from '../components/Button.js';
import { Sheet } from '../components/Sheet.js';
import { useLang, useT, type T } from '../i18n/index.js';
import { api } from '../net/api.js';
import { socket } from '../net/socket.js';
import { useApp } from '../state/store.js';
import { band, chips, bps as bpsText, eta } from './lib/format.js';
import { lastStake, rememberStake } from './lib/prefs.js';
import { Screen } from './parts/Screen.js';
import { StationArt } from './parts/StationArt.js';
import './Venue.css';

/**
 * S4 — the station up close: three stake tiers, who is sitting, and the way in.
 * S5 (the buy-in sheet) lives here too, because the sheet is the same decision
 * continued — you never leave the table you are looking at.
 */
export function Venue({ game }: { game: GameId }): JSX.Element {
  const venue = VENUES[game];
  const go = useApp((s) => s.go);
  const balance = useApp((s) => s.balance);
  const queue = useApp((s) => s.queue);
  const status = useApp((s) => s.status);
  const setToast = useApp((s) => s.setToast);
  const t = useT();
  const lang = useLang();

  const [lobby, setLobby] = useState<LobbyResponse | null>(null);
  const [lobbyDown, setLobbyDown] = useState(false);
  const [sheetTier, setSheetTier] = useState<StakeTier | null>(null);

  useEffect(() => {
    let alive = true;
    let first = true;
    /*
     * Polled, not fetched once. The room list below refreshes every few seconds
     * and these counters did not, so the tier card sat on "Wala pang laro dito"
     * above a list showing a game in progress. Two readings of the same venue
     * on one screen have to agree.
     */
    const pull = (): void => {
      api
        .lobby()
        .then((r) => {
          if (!alive) return;
          setLobby(r);
          setLobbyDown(false);
        })
        .catch(() => {
          if (!alive) return;
          setLobbyDown(true);
          // Only on the first failure: a poll that toasts every four seconds
          // turns a flaky connection into a wall of warnings.
          if (first) setToast(t('lobby.venue.tables.error'), 'warn');
        })
        .finally(() => {
          first = false;
        });
    };
    pull();
    const timer = setInterval(pull, 4_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [setToast]);

  const live = lobby?.venues.find((v) => v.game === game) ?? null;
  const queuedHere = queue?.game === game ? queue : null;

  /**
   * The same gate the compound applies, applied again here. `queue.join`
   * escrows the buy-in immediately, so sitting down at a venue the server has
   * not opened costs chips before it fails — the button stays shut until the
   * lobby says the venue is live. The wave only stands in before the first
   * lobby response; after that the server is the authority, or a venue whose
   * rules have shipped stays shut on a client that has not been rebuilt.
   */
  const openState: 'live' | 'soon' | 'offline' | 'loading' = lobbyDown
    ? 'offline'
    : lobby === null
      ? venue.wave > 1
        ? 'soon'
        : 'loading'
      : live?.available
        ? 'live'
        : 'soon';
  const canSit = openState === 'live';

  return (
    <Screen title={t(`venue.${game}`)} sub={t(`venue.${game}.station`)}>
      <div className="venue" style={{ '--accent': `var(--venue-${game})` } as CSSProperties}>
        <div className="venue__banner">
          <StationArt game={game} />
          <div className="venue__banner-copy">
            <b>{t(`lobby.venue.${game}.tag`)}</b>
            <span>
              {t('lobby.venue.meta', {
                n: venue.seats[0] ?? 2,
                secs: Math.round(venue.turnMs / 1000),
              })}
            </span>
          </div>
        </div>

        {canSit ? null : (
          <p className="s-note venue__shut">{shutText(openState, t)}</p>
        )}

        {queuedHere ? (
          <div className="s-panel queue">
            <div className="queue__dots">
              <i />
              <i />
              <i />
            </div>
            <div className="s-row__main">
              <b>{t('lobby.queue.in', { tier: t(`stake.${queuedHere.tier}`) })}</b>
              <span>
                {t('lobby.queue.detail', {
                  n: queuedHere.position,
                  eta: eta(queuedHere.etaMs, t),
                })}
              </span>
            </div>
            <Button variant="secondary" onClick={() => socket.send({ t: 'queue.leave' })}>
              {t('lobby.queue.leave')}
            </Button>
          </div>
        ) : null}

        {/*
          Two regions with two jobs. The left is the decision — three stakes,
          one row each, because the data is row-shaped (a name, a band, seats,
          one button) and pouring rows into cards is what filled a wide screen
          with upholstered emptiness. The right is the life of the venue: the
          actual tables, refreshing on their own clock, with the private-room
          door underneath. On anything narrower than the grid can afford, the
          regions stack in exactly the old reading order.
        */}
        <div className="venue__grid">
          <div className="venue__pick">
            {STAKE_TIERS.map((tier) => (
              <TierCard
                key={tier}
                game={game}
                tier={tier}
                lobby={live}
                busy={queue !== null || !canSit}
                onSit={() => setSheetTier(tier)}
              />
            ))}
          </div>

          <aside className="venue__life">
            <TableList
              game={game}
              canSit={canSit && queue === null}
              onToast={setToast}
            />

            <div className="s-panel s-panel--quiet">
              <div className="s-panel__head">
                <h2>{t('lobby.kubo.title')}</h2>
                <p>{t('lobby.kubo.sub')}</p>
              </div>
              <p className="s-note">{t('lobby.kubo.note')}</p>
              <div className="s-actions">
                <Button variant="secondary" onClick={() => go({ s: 'kubo' })}>
                  {t('lobby.kubo.open')}
                </Button>
              </div>
            </div>
          </aside>
        </div>

        <Sheet
          open={sheetTier !== null}
          onClose={() => setSheetTier(null)}
          title={t('lobby.buyin.title')}
          footnote={lang === 'en' ? NO_CASH_VALUE_DISCLOSURE : NO_CASH_VALUE_DISCLOSURE_TL}
        >
          {sheetTier ? (
            <BuyIn
              game={game}
              tier={sheetTier}
              balance={balance}
              connected={status === 'open'}
              onClose={() => setSheetTier(null)}
              onGo={go}
              onToast={setToast}
            />
          ) : null}
        </Sheet>
      </div>
    </Screen>
  );
}

/**
 * The rooms at this venue, named rather than counted.
 *
 * Matchmaking is still the fast path and stays first on the screen — most
 * players want a game, not a lobby. This is for the ones who want *that* game:
 * the table with two humans already in it rather than a fresh one that bots
 * will fill. Live tables stay listed so a busy venue looks busy; you cannot sit
 * at one, but you can watch it.
 */
function TableList({
  game,
  canSit,
  onToast,
}: {
  game: GameId;
  canSit: boolean;
  onToast: (text: string, tone?: 'info' | 'warn' | 'win') => void;
}): JSX.Element | null {
  const go = useApp((s) => s.go);
  const t = useT();
  const [tables, setTables] = useState<OpenTable[] | null>(null);

  useEffect(() => {
    let alive = true;
    const pull = (): void => {
      api
        .venueTables(game)
        .then((r) => alive && setTables(r.tables))
        .catch(() => alive && setTables([]));
    };
    pull();
    // Rooms fill and empty while you are reading them; a stale list sends a
    // player at a table that is already full.
    const timer = setInterval(pull, 4_000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [game]);

  if (tables === null || tables.length === 0) return null;

  return (
    <div className="s-panel">
      <div className="s-panel__head">
        <h2>{t('lobby.rooms.title')}</h2>
        <p>{t('lobby.rooms.sub')}</p>
      </div>

      <ul className="rooms">
        {tables.map((table) => {
          const full = table.taken >= table.seatCount;
          const playing = table.status === 'live';
          const joinable = canSit && !playing && !full;
          return (
            <li className="rooms__row" key={table.tableId} data-live={playing}>
              <div className="rooms__who">
                <b>
                  {t('lobby.rooms.stake', {
                    tier: t(`stake.${table.tier}`),
                    chips: chips(table.buyIn),
                  })}
                </b>
                <span>{whoText(table, t)}</span>
              </div>

              <div
                className="rooms__seats"
                aria-label={t('lobby.rooms.seated', { taken: table.taken, total: table.seatCount })}
              >
                {Array.from({ length: table.seatCount }, (_, i) => (
                  <i
                    key={i}
                    data-taken={i < table.taken}
                    data-bot={i >= table.humans && i < table.taken}
                  />
                ))}
              </div>

              {joinable ? (
                <Button
                  variant="secondary"
                  onClick={() => socket.send({ t: 'table.sit', tableId: table.tableId })}
                >
                  {t('lobby.rooms.sit')}
                </Button>
              ) : playing ? (
                <Button
                  variant="ghost"
                  onClick={() => {
                    socket.send({ t: 'spectate.join', tableId: table.tableId });
                    go({ s: 'table', tableId: table.tableId });
                    onToast(t('lobby.rooms.watching'), 'info');
                  }}
                >
                  {t('lobby.rooms.watch')}
                </Button>
              ) : (
                <span className="s-tag">{full ? t('lobby.rooms.full') : t('lobby.rooms.shut')}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Who is at the table, in the terms a player scanning a lobby cares about. */
function whoText(table: OpenTable, t: T): string {
  if (table.taken === 0) return t('lobby.rooms.empty');
  const names = table.seats.filter((s) => !s.isBot).map((s) => s.handle);
  const bots = table.taken - table.humans;
  const people =
    names.length === 0
      ? t('lobby.rooms.allai')
      : names.length === 1
        ? (names[0] ?? '')
        : names.length === 2
          ? t('lobby.rooms.two', { a: names[0] ?? '', b: names[1] ?? '' })
          : `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  return bots > 0 ? t('lobby.rooms.withbots', { who: people, n: bots }) : people;
}

function shutText(state: 'soon' | 'offline' | 'loading', t: T): string {
  switch (state) {
    case 'offline':
      return t('lobby.venue.shut.offline');
    case 'loading':
      return t('lobby.venue.shut.loading');
    case 'soon':
      // All six venues have shipped, so this is only reachable if the server
      // cannot load an engine. Naming another venue as "the open one" was wave-1
      // copy that would now be simply false.
      return t('lobby.venue.shut.soon');
  }
}

interface TierCardProps {
  game: GameId;
  tier: StakeTier;
  lobby: LobbyResponse['venues'][number] | null;
  busy: boolean;
  onSit: () => void;
}

function TierCard({ game, tier, lobby, busy, onSit }: TierCardProps): JSX.Element {
  const def = STAKES[tier];
  const venue = VENUES[game];
  const t = useT();
  const row = lobby?.tables.find((t) => t.tier === tier) ?? null;
  /*
   * What a table here will actually seat, not the largest size the engine could
   * handle. `VENUES.pusoy.seats` is [2,3,4] and the table service deals
   * `seats[0]`, so the four dots promised a four-handed game that the server has
   * never once built — two panels above a buy-in sheet saying "2 manlalaro".
   */
  const seatCount = venue.seats[0] ?? 2;
  const open = row?.open ?? 0;
  const liveTables = row?.live ?? 0;

  return (
    <section className="tier">
      <div className="tier__id">
        <b>{t(`stake.${tier}`)}</b>
        <span className="s-tag">{t('lobby.tier.tong', { pct: bpsText(def.tongBps) })}</span>
        {!def.kuboAllowed ? (
          <span className="s-tag s-tag--soon">{t('lobby.tier.nokubo')}</span>
        ) : null}
      </div>

      <p className="tier__band">
        {band(def.minBuyIn, def.maxBuyIn)}
        <small>{t('lobby.tier.band')}</small>
      </p>

      {/*
        The lobby reports tables, not chairs, so the dots show how many chairs a
        table of this game seats, and the line next to them says whether any of
        those tables still has room. Nothing here invents an occupancy we were
        not told. What this row deliberately does NOT say any more is "no games
        running here": the table list beside it is the one place that fact
        lives, and the old foot note managed to contradict it on the same
        screen — a tier with an open bot table read "1 table has room" and
        "no games running here" three lines apart.
      */}
      <div className="tier__seats">
        {Array.from({ length: seatCount }, (_, i) => (
          <span key={i} className="tier__seat" data-taken={open === 0 && liveTables > 0 ? 'true' : 'false'} />
        ))}
        <span className="s-note" style={{ marginLeft: 6 }}>
          {open > 0
            ? t('lobby.tier.room', { n: open })
            : liveTables > 0
              ? t('lobby.tier.allfull')
              : t('lobby.tier.none')}
        </span>
      </div>

      <Button variant="primary" onClick={onSit} disabled={busy}>
        {t('lobby.tier.sit')}
      </Button>
    </section>
  );
}

interface BuyInProps {
  game: GameId;
  tier: StakeTier;
  balance: number;
  connected: boolean;
  onClose: () => void;
  onGo: ReturnType<typeof useApp.getState>['go'];
  onToast: ReturnType<typeof useApp.getState>['setToast'];
}

/** S5 — buy-in. The wallet check happens here, honestly, before you sit. */
function BuyIn({ game, tier, balance, connected, onClose, onGo, onToast }: BuyInProps): JSX.Element {
  const def = STAKES[tier];
  const t = useT();
  const [amount, setAmount] = useState(() => lastStake(game, tier, def.minBuyIn, def.maxBuyIn));
  const step = useMemo(() => Math.max(100, Math.round((def.maxBuyIn - def.minBuyIn) / 10)), [def]);
  const short = balance < amount;

  /**
   * What this stake is actually for. The tier band tells a player what they
   * are putting in; nothing told them what they were playing for, which on a
   * four-seat table is four times the number they were looking at. The tong is
   * taken off here rather than mentioned as a percentage, because a receipt
   * line nobody can do the arithmetic on is not a disclosure (§7.2).
   */
  const seats = VENUES[game].seats[0] ?? 2;
  const pot = amount * seats;
  const tong = Math.floor((pot * def.tongBps) / 10_000);

  /** Sitting down is the moment the stake is a real choice, so remember it then. */
  const take = (message: { t: 'queue.join' | 'queue.solo'; game: GameId; tier: StakeTier; buyIn: number }): void => {
    if (!connected) {
      onToast(t('lobby.buyin.offline'), 'warn');
      return;
    }
    rememberStake(game, tier, amount);
    socket.send(message);
    onClose();
  };

  return (
    <div className="buyin">
      <div>
        <p className="buyin__amount">
          {chips(amount)}{' '}
          <span>{t('lobby.buyin.amount', { tier: t(`stake.${tier}`) })}</span>
        </p>
        <p className="s-note">
          {t('lobby.buyin.where', {
            game: t(`venue.${game}`),
            station: t(`venue.${game}.station`),
            n: seats,
          })}
        </p>
      </div>

      <div className="s-field">
        <label htmlFor="buyin-range">
          {t('lobby.buyin.pick', { band: band(def.minBuyIn, def.maxBuyIn) })}
        </label>
        <input
          id="buyin-range"
          className="s-range"
          type="range"
          min={def.minBuyIn}
          max={def.maxBuyIn}
          step={step}
          value={amount}
          onChange={(e) => setAmount(Number(e.currentTarget.value))}
        />
        {/* The slider is a poor instrument on a phone held sideways in one hand. */}
        <div className="buyin__presets">
          {[def.minBuyIn, Math.round((def.minBuyIn + def.maxBuyIn) / 2), def.maxBuyIn].map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={amount === v}
              onClick={() => setAmount(v)}
            >
              {chips(v)}
            </button>
          ))}
        </div>
      </div>

      {/* What you are competing for, at this stake, with the house cut removed. */}
      <p className="buyin__pot">
        <span>{t('lobby.buyin.iffull')}</span>
        <span className="s-row__data">
          <b>{chips(pot - tong)}</b> {t('lobby.buyin.playfor')}
        </span>
        <small>
          {t('lobby.buyin.potnote', {
            pot: chips(pot),
            pct: bpsText(def.tongBps),
            tong: chips(tong),
          })}
        </small>
      </p>

      <p className="buyin__wallet">
        <span>{t('lobby.buyin.wallet')}</span>
        <span className="s-row__data">{chips(balance)}</span>
      </p>

      {short ? (
        <div className="buyin__short">
          <b>{t('lobby.buyin.short')}</b>
          <span className="s-note">
            {t('lobby.buyin.shortnote', { n: chips(amount - balance) })}
          </span>
          <div className="s-actions">
            <Button
              variant="secondary"
              onClick={() => {
                onClose();
                onGo({ s: 'wallet' });
              }}
            >
              {t('lobby.buyin.baon')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                onClose();
                onGo({ s: 'store' });
              }}
            >
              {t('lobby.buyin.store')}
            </Button>
          </div>
        </div>
      ) : null}

      {/*
        Two doors, one buy-in. Solo is not a cheaper or safer table — same
        chips, same pot, same tong, same ledger (PRD §8.1, §9) — so the choice
        belongs here beside the confirm rather than dressed up as a mode. The
        only difference is that nobody is being waited for.

        No explanatory line under it: this sheet is 369px tall on the landscape
        phone the app is built around, and a sentence here pushed all three
        buttons off the bottom. The one slider above governs both doors, which
        is the fact that actually needed saying.
      */}
      <div className="s-actions">
        <Button
          variant="primary"
          disabled={short}
          onClick={() => take({ t: 'queue.join', game, tier, buyIn: amount })}
        >
          {t('lobby.buyin.sit', { chips: chips(amount) })}
        </Button>
        <Button
          variant="secondary"
          disabled={short}
          onClick={() => take({ t: 'queue.solo', game, tier, buyIn: amount })}
        >
          {t('lobby.buyin.solo')}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          {t('lobby.buyin.cancel')}
        </Button>
      </div>
    </div>
  );
}
